import React, { useState, useEffect } from "react";
import { postRequest } from "services/api";
import Swal from "sweetalert2";

const CheckAttendancePage = ({ onSuccess, onError }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleQRCodeScan = async (qrCodeData) => {
    if (!qrCodeData.trim()) return;

    setIsSubmitting(true);

    try {
      const response = await postRequest("/ahr/absen/scan", {
        qrcode: qrCodeData.trim(),
      });

      if (response.success) {
        // Show success message
        setSuccessMessage(`✅ QR Code berhasil dipindai - Kehadiran dicatat`);
        setShowSuccess(true);

        // Call success callback
        onSuccess();

        // Auto-reset after 3 seconds
        setTimeout(() => {
          setShowSuccess(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        // Handle all API error responses with Swal popup
        if (response.code === 422 && response.errors) {
          // Format validation errors for Swal popup
          const errorMessages = Object.entries(response.errors)
            .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
            .join("\n");

          Swal.fire({
            title: "Kesalahan Validasi",
            text: errorMessages,
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#dc3545"
          });
        } else {
          // Show other API error messages (like 400 errors)
          Swal.fire({
            title: "Kesalahan",
            text: response.message || "Gagal memproses QR Code",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#dc3545"
          });
        }
      }
    } catch (error) {
      console.error("QR Code scan error:", error);

      // Show network or other error in Swal popup
      Swal.fire({
        title: "Kesalahan Jaringan",
        text: error.message || error.error || error.errors?.qrcode?.[0] || "Terjadi kesalahan saat memproses QR Code. Silakan coba lagi.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#dc3545"
      });

      onError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("id-ID", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div
      className="d-flex flex-column"
      style={{ height: "100vh", backgroundColor: "#f8f9fa" }}
    >
      <div className="flex-grow-1 p-3">
        {/* Header */}
        <div className="text-center mb-4">
          <h3 className="mb-1">📋 Daily Attendance</h3>
          <div className="text-muted mb-2">{formatDate(currentTime)}</div>
          <div className="h4 text-primary font-monospace">
            {currentTime.toLocaleTimeString("id-ID")}
          </div>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div
            className="alert alert-success alert-dismissible fade show mb-4"
            role="alert"
          >
            <div className="d-flex align-items-center">
              <div className="me-3">
                <div className="display-6">🎉</div>
              </div>
              <div>
                <h5 className="alert-heading mb-1">Kehadiran Dicatat!</h5>
                <p className="mb-0">{successMessage}</p>
                <small className="text-muted">
                  Kembali ke mode scan dalam beberapa detik...
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setShowSuccess(false);
                setSuccessMessage("");
              }}
            ></button>
          </div>
        )}

        {/* QR Code Scan */}
        <div className="mb-4">
          <label className="form-label fw-bold">Scan QR Code</label>
          <div className="card border-info shadow-sm">
            <div className="card-body text-center p-4">
              {/* QR Code Visual */}
              <div className="mb-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle bg-info bg-opacity-10"
                  style={{
                    width: "120px",
                    height: "120px",
                    border: "3px solid #0dcaf0",
                    boxShadow: "0 4px 15px rgba(13, 202, 240, 0.2)"
                  }}
                >
                  <div className="text-info" style={{ fontSize: "3rem" }}>📱</div>
                </div>
              </div>

              {/* Title and Description */}
              <h5 className="text-info mb-2 fw-bold">Scan QR Code</h5>
              <p className="text-muted mb-4 small">
                Arahkan kamera ke QR Code untuk mencatat kehadiran secara otomatis
              </p>

              {/* Scan Button */}
              <div className="d-grid gap-3">
                <button
                  className="btn btn-info btn-lg d-flex align-items-center justify-content-center gap-2 py-3"
                  onClick={() => {
                    // Simulate QR code scan (in real implementation, this would open camera)
                    const mockQRCode = prompt("Masukkan data QR Code (untuk simulasi):");
                    if (mockQRCode && mockQRCode.trim()) {
                      handleQRCodeScan(mockQRCode.trim());
                    }
                  }}
                  disabled={isSubmitting}
                  style={{
                    borderRadius: "12px",
                    fontWeight: "600",
                    boxShadow: "0 4px 15px rgba(13, 202, 240, 0.3)",
                    transition: "all 0.3s ease"
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm"></span>
                      <span>Memproses QR Code...</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: "1.2rem" }}>📱</span>
                      <span>Scan QR Code</span>
                    </>
                  )}
                </button>

                {/* Alternative Input */}
                <div className="text-center">
                  <small className="text-muted">atau</small>
                </div>

                <div className="border rounded p-3 bg-light">
                  <label className="form-label small fw-bold text-muted mb-2">
                    Masukkan Kode QR Manual
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm text-center"
                    placeholder="Ketik kode QR..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        handleQRCodeScan(e.target.value.trim());
                        e.target.value = '';
                      }
                    }}
                    disabled={isSubmitting}
                    style={{ fontSize: "0.9rem", cursor: "text" }}
                  />
                  <small className="text-muted mt-1 d-block">
                    Tekan Enter untuk memproses
                  </small>
                </div>
              </div>

              {/* Tips */}
              <div className="mt-4 pt-3 border-top">
                <div className="row g-2 text-start">
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center">
                      <span className="badge bg-success me-2">✓</span>
                      Cepat & Otomatis
                    </small>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center">
                      <span className="badge bg-success me-2">✓</span>
                      Tanpa Kontak
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckAttendancePage;
